import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import ServiceSection from "@/components/ServiceSection";
import SEO from "@/components/SEO";
import FAQSection from "@/components/FAQSection";
import { 
  Heart, 
  Video, 
  Palette, 
  Camera, 
  Globe, 
  MessageSquare,
  Lightbulb,
  Users,
  TrendingUp,
  Award,
  Play
} from "lucide-react";

const ValuesPage = () => {
  const services = [
    {
      icon: Lightbulb,
      title: "Strategy & Value Development",
      description: "We work with your leadership team to define, refine, or revitalise your company values. Through workshops, stakeholder interviews, and cultural analysis, we create a values framework that genuinely reflects who you are and where you're going.",
      services: [
        "Values discovery workshops",
        "Stakeholder consultation",
        "Cultural audit and analysis",
        "Messaging framework development",
        "Implementation roadmap planning"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/ix379qai_IMG_0070.jpg",
      altText: "Internal communications strategy workshop for company values and ethos development"
    },
    {
      icon: Video,
      title: "Video Production",
      description: "From documentary-style films showcasing your culture to bite-sized values content for internal screens, we produce cinema-quality video that brings your ethos to life. Real employees, real stories, real impact.",
      services: [
        "Documentary-style culture films",
        "Employee story features",
        "Leadership vision pieces",
        "Values launch event coverage",
        "Motion graphics and animation"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/yn3cs337_shaun%20%282%29.jpg",
      altText: "Professional video production crew filming employee values content for internal communications"
    },
    {
      icon: Palette,
      title: "Graphic Design",
      description: "Your values deserve to be visible everywhere. We create striking visual assets that reinforce your culture across offices, factories, and digital platforms. From wall graphics to desktop wallpapers.",
      services: [
        "Values branding and identity",
        "Office and site signage",
        "Digital screen content",
        "Print materials and posters",
        "Presentation templates"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/gfwo25j4_brochure%20%28Copy%29.jpg",
      altText: "Internal communications graphic design brochure for company values campaign"
    },
    {
      icon: Camera,
      title: "Professional Photography",
      description: "Your people see straight through stock imagery. We capture real employees in real workplaces, creating authentic visuals that staff actually recognise and connect with — not generic faces they'll never meet.",
      services: [
        "Employee portrait sessions",
        "Workplace lifestyle photography",
        "Event and milestone coverage",
        "Leadership team shoots",
        "Bespoke image library creation"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/56x7aii1_IMG_0234%20low.jpg",
      altText: "Authentic employee photography replacing stock images for internal communications"
    },
    {
      icon: MessageSquare,
      title: "Social Media Content",
      description: "Internal social platforms need constant, quality content to drive engagement. We create scroll-stopping posts that celebrate your values and keep your culture conversations alive.",
      services: [
        "Internal social strategy",
        "Content calendar planning",
        "Employee spotlight series",
        "Values milestone celebrations",
        "Engagement campaign design"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/wa19h2g6_content%20creation.jpg",
      altText: "Content creation and social media design for employee engagement campaigns"
    },
    {
      icon: Globe,
      title: "Website Development",
      description: "Whether it's a dedicated culture microsite or values sections on your intranet, we build digital experiences that immerse employees in your ethos and make your values accessible anywhere.",
      services: [
        "Culture and values microsites",
        "Intranet section development",
        "Interactive values experiences",
        "Employee recognition portals",
        "Mobile-responsive design"
      ],
      image: "https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/a2ifgxz4_mock-4-scaled.jpg",
      altText: "Internal communications website development for company values portal",
      imageContain: true,
      imageBg: "#8c8c8c"
    }
  ];

  const benefits = [
    {
      icon: Users,
      title: "Unified Workforce",
      description: "Staff who understand and embrace your values work better together, regardless of location or department."
    },
    {
      icon: TrendingUp,
      title: "Improved Retention",
      description: "Employees who connect with company values stay longer, reducing costly turnover and recruitment."
    },
    {
      icon: Award,
      title: "Stronger Culture",
      description: "Clear, visible values create a culture that attracts like-minded talent and builds brand advocates."
    }
  ];

  return (
    <div data-testid="values-page" className="min-h-screen bg-[#050505]">
      <SEO
        title="Staff Engagement & Internal Communications | Boost Employee Connection"
        description="Drive meaningful staff engagement across your organisation. We create internal communications campaigns, videos, design and content that connect employees to your culture, improve retention and boost engagement for large organisations."
        keywords="staff engagement, employee engagement, internal communications, organisational culture, staff retention, employee connection, internal comms, corporate engagement strategy, culture change, workforce engagement"
        canonical="/staff-engagement"
      />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center" data-testid="values-hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920"
            alt="Internal communications team planning staff engagement campaign for large organisation"
            className="w-full h-full object-cover"
            decoding="async"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-transparent" />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10 pt-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center">
                <Heart className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <span className="text-[#00E5FF] font-semibold uppercase tracking-wider">
                Internal Communications
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-none text-white mb-6">
              STAFF ENGAGEMENT
            </h1>

            <p className="text-lg md:text-xl text-[#B3B3B3] max-w-2xl mb-8">
              Most companies have values. The problem is, most employees can't name them. 
              Your values already exist — we make sure your people actually know them, believe in them, 
              and see them reflected in everything your organisation does.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button
                  data-testid="values-book-call-btn"
                  className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-6 uppercase tracking-wider text-base"
                >
                  <Play className="w-5 h-5 mr-2 fill-black" />
                  Book a Call
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button
                  data-testid="values-case-studies-btn"
                  variant="outline"
                  className="bg-white/10 text-white hover:bg-white/20 border-white/10 px-8 py-6 uppercase tracking-wider text-base"
                >
                  View Case Studies
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-10 md:py-16 bg-[#0a0a0a]" data-testid="values-problem">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-5xl tracking-wide text-white mb-8">
              YOUR VALUES ARE YOUR FOUNDATION.
              <br />
              <span className="text-[#00E5FF]">BUT DOES YOUR TEAM KNOW THEM?</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg leading-relaxed mb-8">
              Too many businesses have values that live on a poster in reception and 
              nowhere else. Your people can't live values they don't understand, can't 
              remember, or never see. When staff feel disconnected from your company's 
              purpose, engagement drops, retention suffers, and culture fractures.
            </p>
            <p className="text-white text-xl font-medium">
              We change that. Through strategic communications, powerful storytelling, 
              and consistent visual presence, we make your values impossible to ignore.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 md:py-16 bg-[#050505]" data-testid="values-benefits">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="p-8 bg-[#141414] rounded-lg border border-[#1F1F1F] hover:border-[#00E5FF]/30 transition-colors"
                data-testid={`benefit-${index}`}
              >
                <div className="w-14 h-14 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-[#00E5FF]" />
                </div>
                <h3 className="font-display text-xl tracking-wide text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="text-[#B3B3B3]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-10 md:py-16 bg-[#0a0a0a]" data-testid="values-services">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-4">
              WHAT WE DELIVER
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-2xl mx-auto">
              A complete suite of services to communicate your values effectively, 
              consistently, and memorably.
            </p>
          </div>

          <div className="space-y-20">
            {services.map((service, index) => (
              <ServiceSection
                key={index}
                {...service}
                reverse={index % 2 === 1}
                accentColor="#00E5FF"
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <FAQSection faqs={[
        { question: "How do you communicate company values to a large workforce?", answer: "Through a combination of video content, visual campaigns, internal events and digital communications tailored to different employee groups. We create authentic, engaging content that makes values feel relevant and real — not just words on a wall." },
        { question: "Why do employees disengage from company values?", answer: "Usually because values are communicated once during onboarding and never revisited, or they feel generic and disconnected from day-to-day work. Ongoing, creative campaigns that show values in action through real employee stories keep them alive and meaningful." },
        { question: "How does embedding values improve staff retention?", answer: "Employees who understand and connect with their organisation's values feel a stronger sense of belonging and purpose. This emotional connection is one of the strongest drivers of retention — people stay where they feel aligned with the culture." },
        { question: "Can you help rebrand or refresh existing company values?", answer: "Yes. We often work with organisations to relaunch values that have become stale or meaningless. We develop new visual identities, video campaigns and engagement programmes that re-energise your values and make them resonate with your current workforce." }
      ]} />

      <section className="py-20 md:py-32 bg-[#050505] relative overflow-hidden" data-testid="values-cta">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00E5FF] rounded-full blur-[150px]" />
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-6">
              READY TO BRING YOUR
              <br />
              <span className="text-[#00E5FF]">VALUES TO LIFE?</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg mb-8">
              Let's discuss how we can help your team connect with the values that 
              make your business unique.
            </p>
            <Link to="/contact">
              <Button
                data-testid="values-cta-btn"
                className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-10 py-6 uppercase tracking-wider text-base"
              >
                Start Your Values Journey
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ValuesPage;
